import { useQuery } from "@apollo/client";
import { getYear, lightFormat, parseISO, subYears } from "date-fns";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import { palette, shades } from "lib/miolo";
import IpesQuery from "lib/queries/IpesQuery";
import type { IIpesQuery, IIpesQueryVariables } from "lib/queries/__generated__/IpesQuery.generated";
import ClipLoader from "react-spinners/ClipLoader";
import tinycolor from "tinycolor2";

interface IIpesProps {
  issuer: IBrazilianCompanyIssuerTopLevelFragment;
}

export default function Ipes({ issuer }: IIpesProps) {
  const { data, loading } = useQuery<IIpesQuery, IIpesQueryVariables>(IpesQuery, {
    variables: {
      issuerCnpj: issuer.cnpj,
      deliveryDateStart: lightFormat(subYears(new Date(), 1), "yyyy-MM-dd"),
      deliveryDateEnd: lightFormat(new Date(), "yyyy-MM-dd"),
      filterByCategories: null,
      filterByHasSubjects: null,
      limit: 30,
      cursor: null,
    },
  });

  if (loading) {
    return (
      <div
        css={{
          padding: 24,
          textAlign: "center",
        }}
      >
        <ClipLoader size={28} color={palette.royalBlue} loading={true} />
      </div>
    );
  }

  return (
    <div
      css={{
        padding: 8,
        overflow: "hidden",
        backgroundColor: palette.white,
        borderRadius: 4,
      }}
    >
      <table
        css={{
          minWidth: "100%",
          fontSize: 14,
          fontFamily: "museo-sans-condensed",
          lineHeight: 1.33,
          letterSpacing: 0.6,
          textAlign: "left",
          textIndent: 0,
          borderColor: "inherit",
          borderCollapse: "collapse",
          "& > thead": {
            backgroundColor: palette.white,
          },
          "& th": {
            padding: "12px 16px",
            color: shades.greys.variants[2],
            fontWeight: 600,
            textTransform: "uppercase",
          },
          "& tr > td": {
            padding: "12px 16px",
          },
          "& > tbody": {
            "& tr": {
              borderColor: palette.fiord,
              borderTopWidth: 1,
              "&:hover": {
                backgroundColor: tinycolor(palette.catskillWhite).lighten(2).toHexString(),
                "& td:first-of-type": {
                  borderTopLeftRadius: 6,
                  borderBottomLeftRadius: 6,
                },
                "& td:last-of-type": {
                  borderTopRightRadius: 6,
                  borderBottomRightRadius: 6,
                },
              },
            },
          },
          "& tbody > tr": {
            borderTop: `solid 1px ${tinycolor(palette.mystic).lighten(3).toHexString()}`,
            borderBottom: "none",
            "&:hover, &:hover + tr": {
              borderTop: "solid 1px transparent",
            },
          },
        }}
      >
        <thead>
          <tr>
            <th>Tipo</th>
            <th css={{ textAlign: "center" }}>Publicado Em</th>
          </tr>
        </thead>

        <tbody>
          {data?.ipes.nodes.map((node) => {
            const deliveryDate = parseISO(node.deliveryDate);
            const yearOfReference = getYear(deliveryDate);

            return (
              <tr key={node.protocol}>
                <td>
                  <div css={{ display: "flex", alignItems: "center" }}>
                    {node.category === "Fato Relevante" && (
                      <div css={{ marginRight: 12 }}>
                        <div
                          css={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 12,
                            height: 12,
                            backgroundColor: palette.mojo,
                            borderRadius: 8,
                          }}
                        />
                      </div>
                    )}
                    {node.category === "Aviso aos Acionistas" && node.type === "Outros avisos" && (
                      <div css={{ marginRight: 12 }}>
                        <div
                          css={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 12,
                            height: 12,
                            backgroundColor: palette.dixie,
                            borderRadius: 8,
                          }}
                        />
                      </div>
                    )}
                    {node.category === "Dados Econômico-Financeiros" && (
                      <div css={{ marginRight: 12 }}>
                        <div
                          css={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 12,
                            height: 12,
                            backgroundColor: palette.scienceBlue,
                            borderRadius: 8,
                          }}
                        />
                      </div>
                    )}
                    {node.category === "Comunicado ao Mercado" && (
                      <div css={{ marginRight: 12 }}>
                        <div
                          css={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 12,
                            height: 12,
                            backgroundColor: palette.amazon,
                            borderRadius: 8,
                          }}
                        />
                      </div>
                    )}

                    <a
                      href={node.url}
                      rel="noopener noreferrer"
                      target="_blank"
                      css={{
                        display: "inline-grid",
                        overflow: "hidden",
                        color: palette.link,
                        whiteSpace: "nowrap",
                        textDecoration: "none",
                        textOverflow: "ellipsis",
                        rowGap: 8,
                        "& > div > span:first-of-type": {
                          borderBottom: "dashed 1px transparent",
                        },
                        "&:hover > div > span:first-of-type": {
                          borderBottom: `dashed 1px`,
                        },
                      }}
                    >
                      <div>
                        <span
                          css={{
                            display: "inline-flex",
                            fontSize: 16,
                            color: shades.greys.variants[2],
                            fontWeight: 500,
                          }}
                        >
                          {node.category}
                        </span>
                      </div>

                      {node.type && (
                        <span css={{ display: "flex", fontSize: 14, color: shades.greys.variants[3] }}>
                          ↪ {node.type || "-"}
                        </span>
                      )}
                    </a>
                  </div>
                </td>

                <td css={{ textAlign: "center" }}>
                  <div css={{ display: "grid", rowGap: 4 }}>
                    <span css={{ color: shades.greys.variants[3], fontWeight: 500, fontSize: 16 }}>
                      {lightFormat(deliveryDate, "dd/MM/yyyy")}
                    </span>

                    <div>
                      <span css={{ fontSize: 14, color: shades.greys.variants[4] }}>Referente à {yearOfReference}</span>
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
